import React, { forwardRef, useState, useEffect, useRef } from "react";
import styles from "./Subscription.module.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

const Subscription = forwardRef((props, ref) => {
  const [isYearly, setIsYearly] = useState(false);

  const containerRef = useRef(null);
  const cardsRef = useRef([]);
  const priceRefs = useRef([]);

  useEffect(() => {
    // Animate the entire container with ScrollTrigger
    gsap.from(containerRef.current, {
      
      duration: 1,
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 80%",
        end: "top 30%",
        scrub: true, // makes the animation linked to scroll
      },
    });

    // Animate each card with ScrollTrigger and stagger effect
    cardsRef.current.forEach((card, i) => {
      gsap.from(card, {
    
        duration: 1,
        scrollTrigger: {
          trigger: card,
          start: "top 85%",
          end: "top 30%",
          scrub: true,
        },
      });
    });
  }, []);

  useEffect(() => {
    // Animate price value change
    gsap.fromTo(
      priceRefs.current,
      { scale: 0.8, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.4, ease: "power2.out" }
    );
  }, [isYearly]);

  const plans = [
    {
      name: "Basic Plan",
      price: isYearly ? "$100" : "$10",
      description: "Get Started with AI Chat",
      features: [
        "Create up to 3 customized ",
        "Enjoy 400 AI Voice Minutes Monthly",
        "Choose from 3 Basic AI Voices",
        "Only one Workspace",
        "Limit to 1 Member only",
      ],
    },
    {
      name: "Standard",
      price: isYearly ? "$250" : "$25",
      description: "Ideal for Growing Businesses",
      features: [
        "Create up to 10 customized ",
        "1000 AI Voice Minutes Monthly",
        "Choose from 10 AI Voices",
        "Up to 3 Workspaces",
        "Support for 5 Members",
      ],
    },
    {
      name: "Pro Plan",
      price: isYearly ? "$490" : "$49",
      description: "Essential AI Chat for Small Businesses",
      features: [
        "Unlimited Voice Agents",
        "Unlimited AI Voice Minutes",
        "Access to All AI Voices",
        "Unlimited Workspaces",
        "Priority Support",
      ],
    },
  ];

  return (
    <div className={styles.container} ref={containerRef}>
      <h2 className={styles.heading}>We’ve got a plan that’s perfect for you</h2>
      <p className={styles.subheading}>Effortlessly post jobs everywhere - let AI find your perfect match</p>
      <div className={styles.toggleContainer}>
        <label className={styles.switch}>
          <input
            type="checkbox"
            checked={isYearly}
            onChange={() => setIsYearly(!isYearly)}
          />
          <div className={styles.slider}>
            <span className={!isYearly ? `${styles.active} ${styles.month}` : ""}>
              Monthly
            </span>
            <span className={isYearly ? `${styles.active} ${styles.year}` : ""}>
              Yearly
            </span>
          </div>
        </label>
      </div>
      <div className={styles.plans}>
        {plans.map((plan, index) => (
          <div
            key={index}
            ref={(el) => (cardsRef.current[index] = el)}
            className={`${styles.card} ${plan.name === "Standard" ? styles.standardCard : ""}`}
          >
            <div
              className={`${styles.planName} ${
                plan.name === "Standard" ? styles.standardPlanName : ""
              }`}
            >
              {plan.name}
            </div>
            <p className={styles.description}>{plan.description}</p>
            <p className={styles.price} ref={(el) => (priceRefs.current[index] = el)}>
              <span className={styles.value}>{plan.price}</span> Per Month
            </p>
            <ul className={styles.features}>
              {plan.features.map((feature, i) => (
                <li key={i}>
                  <span style={{ color: '#1a1e6c', fontSize: '25px', margin: "1px 2px" }}>●</span>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              className={`${styles.button} ${
                plan.name === "Standard" ? styles.standardButton : ""
              }`}
            >
              Select Plan
            </button>
          </div>
        ))}
      </div>
    </div>
  );
});

export default Subscription;
